import React, { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router';
import { CaseData } from '../../types/case';
import { caseApiService, NoMergeSaveResponse } from '../../services/caseApiService';
import { debugLog, debugError } from '../../utils/debug';
import { mergeCaseData } from '../../utils/caseDataMerge';
import { BackgroundSaveService } from '../../services/backgroundSaveService';
import { getCalcs, registerCalcsLoadFailure } from './calcsLoadRegistry';
import { useAlert } from '../AlertContext';
import { useAuthContext, useHasAnyRole } from '../AuthContext';
import { useManualSaveMode, useSystemSettingsContext } from '../SystemSettingsContext';
import { MergePendingModal } from '../../components/MergePendingModal';
import { SaveConflictModal } from '../../components/SaveConflictModal';
import { AutoSavingModal } from '../../components/AutoSavingModal';
import { CaseLoadErrorModal } from '../../components/CaseLoadErrorModal';
import { EditTakeoverModal } from '../../components/EditTakeoverModal';
import { ClientEditingIndicator } from '../../components/ClientEditingIndicator';
import { ProDowngradeLockedModal } from '../../components/ProDowngradeLockedModal';
import type { NonSplitCustodyArrangement } from '../../utils/proDataReasons';
import { BillingReadOnlyModal } from '../../components/BillingReadOnlyModal';
import { CaseTakenOverBy } from '../../hooks/useCaseEditingLock';
import { getTabId } from '../../utils/clientIdentifiers';
import { announceTakeover, announceSaved } from '../../utils/caseEditingPresenceChannel';
import { PendingServerChangesInfo } from '../../types/caseApi';
import { SimpleCaseContext, SimpleCaseContextData, SimpleCaseContextType } from './SimpleCaseContextDef';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useCaseEditingLock } from '../../hooks/useCaseEditingLock';
import { useEffectiveSubscriptionType } from '../../hooks/useEffectiveSubscriptionType';
import { isProDowngradeLocked } from '../../hooks/useProDowngrade';
import { useTeamBillingState, TEAM_BILLING_PATH } from '../../hooks/useTeamBillingState';
import { isTeamBillingReadOnlyError, claimBillingReadOnlyModal } from '../../utils/teamBillingReadOnly';
import { isCopyInProgressError, copyInProgressMessage } from '../../utils/copyInProgress';
import { isCalcVersionEnabled, isCalcVersionLockedFor, isCalcVersionDisabledError } from '../../utils/calcVersionAvailability';
import type { CalcVersionAvailability } from '../../types/calcVersion';

interface SimpleCaseProviderProps {
    children: ReactNode;
}

/**
 * Apply a nested path mutation to a cloned case object. Pure function extracted
 * from updateNestedField so the computation can be moved inside setFormState(prev
 * => ...) where `prev` is always the latest committed state (R11 stale-closure fix).
 *
 * Returns { newData, previousValue } — previousValue is the old value at the path
 * (used for Unsupported-state rollback).
 */
function applyNestedPathMutation(
    source: any,
    path: string,
    value: any
): { newData: any; previousValue: any } {
    const convertObjectToArray = (obj: any): any[] => {
        const numericKeys = Object.keys(obj)
            .map(k => parseInt(k))
            .filter(k => !isNaN(k))
            .sort((a, b) => a - b);
        if (numericKeys.length === 0) return [];
        const maxIndex = Math.max(...numericKeys);
        const array: any[] = [];
        for (let idx = 0; idx <= maxIndex; idx++) {
            array[idx] = obj[idx.toString()];
        }
        return array;
    };

    const keys = path.split('.');
    let previousValue: any;

    // Capture previousValue
    let cur: any = source;
    for (let i = 0; i < keys.length - 1; i++) {
        const k = keys[i];
        cur = /^\d+$/.test(k) ? cur?.[parseInt(k)] : cur?.[k];
    }
    const prevFinalKey = keys[keys.length - 1];
    previousValue = /^\d+$/.test(prevFinalKey)
        ? cur?.[parseInt(prevFinalKey)]
        : cur?.[prevFinalKey];

    const newData = JSON.parse(JSON.stringify(source));
    let current: any = newData;
    let parent: any = null;
    let parentKey: string | null = null;

    for (let i = 0; i < keys.length - 1; i++) {
        const currentKey = keys[i];
        const nextKey = keys[i + 1];
        const isCurrentKeyNumeric = /^\d+$/.test(currentKey);
        const isNextKeyNumeric = /^\d+$/.test(nextKey);

        if (isCurrentKeyNumeric && !Array.isArray(current) && typeof current === 'object' && current !== null) {
            const converted = convertObjectToArray(current);
            if (parent !== null && parentKey !== null) {
                const isParentKeyNumeric = /^\d+$/.test(parentKey);
                if (isParentKeyNumeric && Array.isArray(parent)) {
                    parent[parseInt(parentKey)] = converted;
                } else {
                    parent[parentKey!] = converted;
                }
            }
            current = converted;
        }

        if (isCurrentKeyNumeric) {
            const index = parseInt(currentKey);
            while (current.length <= index) current.push(undefined);
            if (current[index] === undefined || current[index] === null) {
                current[index] = isNextKeyNumeric ? [] : {};
            } else if (isNextKeyNumeric && !Array.isArray(current[index]) && typeof current[index] === 'object') {
                current[index] = convertObjectToArray(current[index]);
            }
            parent = current;
            parentKey = currentKey;
            current = current[index];
        } else {
            if (!current[currentKey]) {
                current[currentKey] = isNextKeyNumeric ? [] : {};
            } else if (isNextKeyNumeric && !Array.isArray(current[currentKey]) && typeof current[currentKey] === 'object') {
                current[currentKey] = convertObjectToArray(current[currentKey]);
            }
            parent = current;
            parentKey = currentKey;
            current = current[currentKey];
        }
    }

    const finalKey = keys[keys.length - 1];
    const isFinalKeyNumeric = /^\d+$/.test(finalKey);

    if (isFinalKeyNumeric) {
        if (!Array.isArray(current) && typeof current === 'object' && current !== null) {
            const converted = convertObjectToArray(current);
            if (parent !== null && parentKey !== null) {
                const isParentKeyNumeric = /^\d+$/.test(parentKey);
                if (isParentKeyNumeric && Array.isArray(parent)) {
                    parent[parseInt(parentKey)] = converted;
                } else {
                    parent[parentKey!] = converted;
                }
            }
            current = converted;
        }
        const index = parseInt(finalKey);
        while (current.length <= index) current.push(undefined);
        current[index] = value;
    } else {
        current[finalKey] = value;
    }

    return { newData, previousValue };
}

/**
 * Extract detailed error message from error object
 * Handles validation errors from ASP.NET Core ModelState format
 */
function extractDetailedErrorMessage(error: any): string {
    // Check if error has details with errors array (ModelState validation errors)
    if (error?.details?.errors && typeof error.details.errors === 'object') {
        const validationErrors: string[] = [];

        // Iterate through the errors object (ModelState format)
        for (const [key, value] of Object.entries(error.details.errors)) {
            if (Array.isArray(value)) {
                // Multiple errors for this field
                value.forEach((err: any) => {
                    if (typeof err === 'string' && err.trim()) {
                        validationErrors.push(err.trim());
                    } else if (err && typeof err === 'object' && 'errorMessage' in err) {
                        validationErrors.push((err as { errorMessage: string }).errorMessage);
                    }
                });
            } else if (typeof value === 'string' && value.trim()) {
                // Single error string for this field
                validationErrors.push(value.trim());
            } else if (value && typeof value === 'object' && 'errorMessage' in value) {
                // Error object with errorMessage property
                validationErrors.push((value as { errorMessage: string }).errorMessage);
            }
        }

        if (validationErrors.length > 0) {
            return validationErrors.join('; ');
        }
    }

    // Fall back to error message or details.message
    if (error instanceof Error) {
        return error.message;
    }

    if (error?.message) {
        return error.message;
    }

    if (error?.details?.message) {
        return error.details.message;
    }

    return 'Save failed. Your changes are not saved. Please try again.';
}

export const SimpleCaseProvider: React.FC<SimpleCaseProviderProps> = ({ children }) => {
    const { caseId } = useParams<{ stateCode: string; caseId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { addAlert } = useAlert();
    const { user: currentUser } = useAuthContext();
    const [currentCase, setCurrentCase] = useState<SimpleCaseContextData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isInitializing, setIsInitializing] = useState(false);
    const [calculationError, setCalculationError] = useState<Error | null>(null);
    const [calcsLoadError, setCalcsLoadError] = useState<string | null>(null);
    const [pendingServerChanges, setPendingServerChanges] = useState<PendingServerChangesInfo | null>(null);
    const [isMerging, setIsMerging] = useState(false);
    // True when the last autosave 412'd (another session saved first) and awaits user
    // resolution via SaveConflictModal -- see resolveSaveConflict below.
    const [saveConflictActive, setSaveConflictActive] = useState(false);
    // True while a same-user collision (see conflictIsSameUser below) is being silently
    // resolved via an unconditional force save -- drives a non-dismissible "Saving..." modal
    // instead of SaveConflictModal. See autoForceSave below.
    const [autoForceSaving, setAutoForceSaving] = useState(false);
    // 15s failsafe: if the silent auto force save hasn't finished by the time this fires, stop
    // waiting and fall back to the normal SaveConflictModal so the user is never stuck on the
    // "Saving..." spinner (e.g. a network stall/offline tab).
    const autoForceSaveFailsafeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Guards against the failsafe and the real flush() both trying to resolve autoForceSaving --
    // whichever happens first wins, the other becomes a no-op.
    const autoForceSaveSettledRef = useRef(false);
    // Set when a live presence poll (useCaseEditingLock) detects ANOTHER user took over editing
    // the case open in this wizard -- drives the non-dismissible EditTakeoverModal. See §17.
    const [takeoverBy, setTakeoverBy] = useState<CaseTakenOverBy | null>(null);
    // ConcurrentCaseEditGuard A2: true when a link-token client is currently active in the expense
    // portal for this case (from the presence poll) -- drives the ClientEditingIndicator.
    const [clientEditingActive, setClientEditingActive] = useState(false);

    // Store etag in a ref to avoid re-renders when it updates (only needed for future saves)
    const etagRef = useRef<string | undefined>(undefined);

    // Auto-save state management - always enabled (hidden from UI)
    const [autoSaveEnabled, setAutoSaveEnabled] = useState<boolean>(true);

    // Manual Save mode: config-only ManualSaveToggle feature flag AND the DB-backed,
    // admin-toggled ManualSaveEnabled setting must both be true. See
    // docs/AUTOSAVE_DETAILS.md "Manual Save mode". Pushed into BackgroundSaveService below
    // so the debounce auto-fire is suppressed without disturbing autoSaveEnabled/dirty-tracking.
    const manualSaveMode = useManualSaveMode();

    useEffect(() => {
        BackgroundSaveService.setManualMode(manualSaveMode);
    }, [manualSaveMode]);

    const toggleAutoSave = useCallback((enabled: boolean) => {
        // Always keep auto-save enabled (UI is hidden)
        setAutoSaveEnabled(true);
        // Still update localStorage for potential future use
        try {
            localStorage.setItem('auto_save_enabled', 'true');
        } catch (error) {
            debugError('Failed to save auto-save preference:', error);
        }
    }, []);

    // Form state management.
    // hasUnsavedChanges + isSaving are derived from BackgroundSaveService below — not stored here —
    // so React state only reflects the truth that the service knows (dirty / inFlight / retry pending).
    const [formState, setFormState] = useState<{
        data: any;
        lastSaved: string | null;
        lastBlurTime: number | null; // Timestamp when text input last blurred (to reset debounce progress bar)
        isEditingTextInput: boolean; // True when actively editing a text/monetary input (before blur)
    }>({
        data: null,
        lastSaved: null,
        lastBlurTime: null,
        isEditingTextInput: false
    });

    // Latest formState available to non-React readers (e.g., BackgroundSaveService snapshotProvider).
    // Synchronously updated on every render so any out-of-band reader sees the freshest state.
    const formStateRef = useRef(formState);
    formStateRef.current = formState;

    // Service-driven flags — single source of truth for "is there work pending" / "is a save in flight".
    const [unsavedFromService, setUnsavedFromService] = useState(false);
    const [savingFromService, setSavingFromService] = useState(false);

    // Stable refs for callbacks that the service-register effect needs but that we don't want to
    // re-register on every change. addAlert is generally stable from AlertContext but we ref it
    // defensively so the registration is truly mount-once.
    const addAlertRef = useRef(addAlert);
    addAlertRef.current = addAlert;

    // Ref to the latest autoForceSave (defined below, near resolveSaveConflict) so the
    // mount-once registration effect's onError can call it without depending on it directly.
    const autoForceSaveRef = useRef<() => void>(() => {});
    // Ref to the ConcurrentCaseEditGuard flag (computed below) for the same mount-once onError:
    // the silent same-user auto-force-save is only appropriate when the guard is on (the takeover
    // modal provides the awareness). With the flag off there's no awareness surface, so a 412 must
    // fall back to the explicit SaveConflictModal rather than silently overwriting the other tab.
    const concurrentCaseEditGuardEnabledRef = useRef(false);

    // ProDowngrade: a case holding Pro-only data is read-only once its team is no longer Pro.
    // containsProData comes from the X-Case-Contains-Pro-Data header on load; proDowngradeLocked
    // latches when the server rejects a save with 403 (the team downgraded while this tab had the
    // case open). The refs mirror the values for loadCase, which is a mount-once useCallback([])
    // and can't close over them.
    const [containsProData, setContainsProData] = useState<boolean | undefined>(undefined);
    const [proDowngradeLocked, setProDowngradeLocked] = useState(false);
    const [proDowngradeLockedDismissed, setProDowngradeLockedDismissed] = useState(false);
    const [proDowngradeCopying, setProDowngradeCopying] = useState(false);
    const proDowngradeLockedRef = useRef(false);
    const proDowngradeEnabledRef = useRef(false);
    const isTeamProRef = useRef(false);

    // BillingDunning: the team's grace period ended with a renewal unpaid, so the server refuses
    // every write. billingLocked latches when a save is refused with 403 TEAM_BILLING_READ_ONLY
    // (the state changed while this tab had the case open); billingReadOnlyRef mirrors the
    // /auth/me state for the mount-once loadCase.
    const [billingLocked, setBillingLocked] = useState(false);
    const [billingLockedDismissed, setBillingLockedDismissed] = useState(false);
    const billingLockedRef = useRef(false);
    const billingReadOnlyRef = useRef(false);

    // Calc version availability: a non-admin may view but not edit a case whose state/year an
    // admin has turned off. calcVersionLockedRef mirrors the render-time lock for the snapshot
    // provider; calcVersionInputsRef carries what loadCase (mount-once) needs to decide it at
    // load. calcVersionRejectedKey latches `${year}-${state}` when the server refuses a save with
    // 403 CalcVersionDisabled (the year was turned off while this tab had the case open).
    const calcVersionLockedRef = useRef(false);
    const calcVersionInputsRef = useRef<{ ready: boolean; calcVersions: CalcVersionAvailability[]; isAdmin: boolean }>({ ready: false, calcVersions: [], isAdmin: false });
    const [calcVersionRejectedKey, setCalcVersionRejectedKey] = useState<string | null>(null);
    // Alimony flag, mirrored for the same reason: passed to recalculate() so it keeps alimony
    // scenarios current with the case's inputs.
    const alimonyEnabledRef = useRef(false);

    // Register the BackgroundSaveService on mount. The service reads the latest case body and
    // etag at execute time via the snapshot provider, so we never have to re-register when state
    // changes. onSuccess advances the etag and updates lastSaved + pendingServerChanges; onError
    // surfaces a toast. Subscription to hasWorkPending() drives the derived hasUnsavedChanges/isSaving.
    useEffect(() => {
        const provider = () => {
            // ProDowngrade: once the server has rejected a save as read-only, stop offering a
            // snapshot at all. runSave() bails on a null snapshot, which is the cheapest way to
            // permanently suppress saving for this case without unwinding the service.
            if (proDowngradeLockedRef.current) return null;
            // BillingDunning: same reasoning -- every save would be refused until the team pays.
            if (billingLockedRef.current) return null;
            // Calc version off: the server refuses non-admin writes. Not a permanent latch -- it
            // lifts once the case moves to an enabled tax year (the edit that does so is allowed).
            if (calcVersionLockedRef.current) return null;
            const data = formStateRef.current.data;
            const cid = data?.basic?.caseId;
            if (!cid) return null;
            return { caseId: cid, caseData: data, etag: etagRef.current ?? '' };
        };
        const callbacks = {
            onSuccess: (response: NoMergeSaveResponse) => {
                etagRef.current = response.etag;
                setCurrentCase(prev => (prev ? { ...prev, etag: response.etag } : prev));
                setFormState(prev => ({ ...prev, lastSaved: new Date().toLocaleTimeString() }));
                // Surface the mandatory import prompt whenever the server reports pending client
                // changes (the MergePendingModal has no dismiss -- the client's submission must be
                // imported). Setting the same value repeatedly is a harmless no-op once it's up.
                if (response.pendingServerChanges) {
                    setPendingServerChanges(response.pendingServerChanges);
                }
                // Tell any same-browser homepage tab this case was just saved so it can refresh
                // that row's "last saved X ago" instantly (no server call). See AUTOSAVE_DETAILS §17.
                const savedCaseId = formStateRef.current.data?.basic?.caseId;
                if (savedCaseId) {
                    announceSaved(savedCaseId, getTabId(), response.updatedAt);
                }
            },
            onError: (error: any) => {
                debugError('BackgroundSaveService: save failed', error);

                // ProDowngrade: the team downgraded to Standard while this tab had a Pro case
                // open. The rejected payload is already preserved -- BackgroundSaveService runs
                // backupQuietly before invoking onError -- and a 403 is not transient, so there
                // is no retry storm. Latch the lock (making the snapshot provider return null),
                // stop the service, and flip the wizard to read-only so the user's only routes
                // forward are copying the case or leaving.
                if (error?.status === 403 && error?.details?.code === 'PRO_DOWNGRADE_READ_ONLY') {
                    proDowngradeLockedRef.current = true;
                    setProDowngradeLocked(true);
                    BackgroundSaveService.deregister();
                    return;
                }

                // BillingDunning: the team's grace period ended unpaid. Not transient either, and
                // the payload was already backed up. Latch, stop saving, and go read-only.
                if (isTeamBillingReadOnlyError(error)) {
                    billingLockedRef.current = true;
                    setBillingLocked(true);
                    BackgroundSaveService.deregister();
                    return;
                }

                // Calc version off: an admin turned this case's state/year off while it was open.
                // The payload was already backed up. Lock the case to view-only for this year and
                // show the server's message; moving to an enabled year unlocks it.
                if (isCalcVersionDisabledError(error)) {
                    const basic = formStateRef.current.data?.basic;
                    calcVersionLockedRef.current = true;
                    setCalcVersionRejectedKey(`${basic?.taxYear}-${basic?.stateCode}`);
                    addAlertRef.current('error', error?.message || 'This tax year is not available yet.');
                    return;
                }

                // R12 — Cross-tab 412 conflict. Another session saved this case first, so our
                // save was rejected with 412 Precondition Failed. This used to auto-recover
                // silently (re-fetch etag, retry, overwrite) with no user awareness of the risk
                // — see docs/AUTOSAVE_DETAILS.md §16/§19. Now: surface SaveConflictModal and let
                // the user choose (Try Again / Make a Copy / Force Save / Cancel) via
                // resolveSaveConflict. The client's formState.data (the user's edits) is always
                // preserved untouched here regardless of which option they pick.
                //
                // Exception: if the server reports the conflicting version was last saved by
                // this SAME user (another tab/device of theirs, not a teammate), skip the modal
                // entirely and silently force-save behind a lightweight "Saving..." indicator --
                // see docs/AUTOSAVE_DETAILS.md §18 and autoForceSave below.
                if (error?.status === 412) {
                    // The blob changed because of a pending client submission (or other pending
                    // server change): NEVER silently force-save -- that would clobber the client's
                    // expenses. Surface the merge flow (MergePendingModal -> triggerMerge, which
                    // reconciles the client's expenses with the user's current edits) and keep the
                    // edits dirty. See docs/AUTOSAVE_DETAILS.md §16/§17.
                    const pending = error?.details?.pendingServerChanges;
                    if (pending && pending.kind) {
                        const mapped: PendingServerChangesInfo = {
                            kind: pending.kind,
                            linkTokenId: pending.linkTokenId ?? undefined,
                            submittedAt: pending.submittedAt ?? undefined,
                        };
                        setPendingServerChanges(mapped);
                        BackgroundSaveService.markDirty({ startDebounce: false });
                        return;
                    }
                    // Silent same-user auto-force-save only when ConcurrentCaseEditGuard is on --
                    // that's the feature that provides the takeover-modal awareness. With it off,
                    // there's no awareness surface, so fall back to the explicit SaveConflictModal
                    // rather than silently overwriting the user's other tab (fix #2).
                    if (error?.details?.conflictIsSameUser === true && concurrentCaseEditGuardEnabledRef.current) {
                        autoForceSaveRef.current();
                        return;
                    }
                    setSaveConflictActive(true);
                    return;
                }

                const msg = extractDetailedErrorMessage(error);
                addAlertRef.current('error', msg);
            },
        };
        BackgroundSaveService.register(provider, callbacks);
        const unsub = BackgroundSaveService.subscribe(() => {
            setUnsavedFromService(BackgroundSaveService.hasWorkPending());
            setSavingFromService(BackgroundSaveService.isSaving());
        });
        return () => {
            unsub();
            BackgroundSaveService.deregister();
            if (autoForceSaveFailsafeRef.current) {
                clearTimeout(autoForceSaveFailsafeRef.current);
                autoForceSaveFailsafeRef.current = null;
            }
        };
    }, []);

    /**
     * Load case data from server (only on initial load)
     */
    const loadCase = useCallback(async (id: string) => {
        setLoading(true);
        setError(null);
        // Wipe any pending dirty state from a previous case before loading fresh data.
        // The gen-bump inside reset() ensures any in-flight save's onSuccess won't stomp
        // the new case's etagRef with the previous case's etag.
        BackgroundSaveService.reset();

        try {
            const response = await caseApiService.getCaseNoMerge(id);

            setContainsProData(response.containsProData);

            // ProDowngrade: fold the lock into the load-time read-only decision, not just the
            // rendered UI. It selects validateInputVersioned over recalculate, so opening a Pro
            // case in a Standard session doesn't derive changes it can never persist -- and it
            // suppresses the two backfill saves below, which would 403.
            const proDowngradeReadOnly = proDowngradeEnabledRef.current
                && !isTeamProRef.current
                && response.containsProData === true;

            const calcVersionInputs = calcVersionInputsRef.current;
            const calcVersionReadOnly = calcVersionInputs.ready && isCalcVersionLockedFor(
                response.data?.basic?.stateCode,
                response.data?.basic?.taxYear,
                calcVersionInputs.calcVersions,
                calcVersionInputs.isAdmin,
            );

            const isReadOnly = response.data?.basic?.caseStatus === 'finalized'
                || response.data?.basic?.caseStatus === 'archived'
                || response.data?.basic?.isLocked === true
                || proDowngradeReadOnly
                || billingReadOnlyRef.current
                || billingLockedRef.current
                || calcVersionReadOnly;

            const { recalculate, validateInputVersioned } = await getCalcs();
            let processedCase: CaseData;
            try {
                processedCase = isReadOnly
                    ? validateInputVersioned(response.data)
                    : recalculate(response.data, { alimonyEnabled: alimonyEnabledRef.current });
                setCalculationError(null);
            } catch (calcError) {
                const error = calcError instanceof Error ? calcError : new Error(String(calcError));
                setCalculationError(error);
                if (error.message.includes('Unsupported state and year')) {
                    processedCase = response.data;
                } else {
                    throw calcError;
                }
            }

            etagRef.current = response.etag;

            // Server reports pending submissions (e.g. client-uploaded expenses) that the user
            // hasn't merged yet. The MergePendingModal will prompt for an explicit merge.
            setPendingServerChanges(response.pendingServerChanges ?? null);

            setCurrentCase({
                case: processedCase,
                etag: response.etag
            });

            setFormState(prev => ({
                ...prev,
                data: processedCase,
                lastSaved: null
            }));

            const splitBackfillCondition = !isReadOnly && processedCase?.children?.custodyArrangement === 'split' && (processedCase?.documents?.splitCases?.length ?? 0) > 0;
            debugLog('SimpleCaseContext: load stepDownCases backfill check', {
                splitBackfillCondition,
                isReadOnly,
                custodyArrangement: processedCase?.children?.custodyArrangement,
                processedSplitCasesLength: processedCase?.documents?.splitCases?.length ?? 0,
                serverSplitCasesLength: response.data?.documents?.splitCases?.length ?? 0,
            });
            if (splitBackfillCondition) {
                const serverSplits = response.data?.documents?.splitCases ?? [];
                let needsPersist = false;
                const perIndex: { i: number; processedLen: number; serverLen: number }[] = [];
                for (let i = 0; i < (processedCase.documents.splitCases?.length ?? 0); i++) {
                    const processedStepDowns = processedCase.documents.splitCases?.[i]?.documents?.stepDownCases ?? [];
                    const serverStepDowns = serverSplits[i]?.documents?.stepDownCases ?? [];
                    perIndex.push({ i, processedLen: processedStepDowns.length, serverLen: serverStepDowns?.length ?? 0 });
                    if (processedStepDowns.length > 0 && serverStepDowns.length === 0) {
                        needsPersist = true;
                        break;
                    }
                }
                debugLog('SimpleCaseContext: load stepDownCases backfill needsPersist', { needsPersist, perIndex, caseId: processedCase.basic?.caseId });
                if (needsPersist && processedCase.basic?.caseId) {
                    debugLog('SimpleCaseContext: Persisting stepDownCases backfill on load', { caseId: processedCase.basic.caseId });
                    try {
                        const saveResponse = await caseApiService.saveCaseNoMerge(
                            processedCase.basic.caseId,
                            processedCase,
                            response.etag ?? ''
                        );
                        etagRef.current = saveResponse.etag;
                        setCurrentCase(prev => prev ? { ...prev, etag: saveResponse.etag } : prev);
                        if (saveResponse.pendingServerChanges) {
                            setPendingServerChanges(saveResponse.pendingServerChanges);
                        }
                        debugLog('SimpleCaseContext: stepDownCases backfill save succeeded');
                    } catch (saveErr) {
                        debugError('SimpleCaseContext: Failed to persist stepDownCases on load (blob not updated)', saveErr);
                    }
                }
            }

            // lineId backfill: normalizeCaseData (inside getCaseNoMerge) mints a random lineId
            // for any legacy financial row missing one. That injection is in-memory only until
            // saved, so a legacy case's row identity would differ on every fresh load/tab until
            // the user's first edit — breaking lineId-keyed reconciliation in the interim. Persist
            // once on load, same one-time-migration pattern as the stepDownCases backfill above.
            // See docs/AUTOSAVE_DETAILS.md section 13.
            if (!isReadOnly && response.lineIdsBackfilled && processedCase.basic?.caseId) {
                debugLog('SimpleCaseContext: Persisting lineId backfill on load', { caseId: processedCase.basic.caseId });
                try {
                    const saveResponse = await caseApiService.saveCaseNoMerge(
                        processedCase.basic.caseId,
                        processedCase,
                        etagRef.current ?? ''
                    );
                    etagRef.current = saveResponse.etag;
                    setCurrentCase(prev => prev ? { ...prev, etag: saveResponse.etag } : prev);
                    if (saveResponse.pendingServerChanges) {
                        setPendingServerChanges(saveResponse.pendingServerChanges);
                    }
                    debugLog('SimpleCaseContext: lineId backfill save succeeded');
                } catch (saveErr) {
                    debugError('SimpleCaseContext: Failed to persist lineId backfill on load (blob not updated)', saveErr);
                }
            }
        } catch (err) {
            debugError('SimpleCaseContext: Error loading case:', err);
            setError(err instanceof Error ? err.message : 'Failed to load case');
            setCurrentCase(null);
        } finally {
            setLoading(false);
        }
    }, []);

    // ProDowngrade: the case was last saved with Pro but this session is not Pro, so it is
    // view-only until copied. proDowngradeLocked covers the mid-session downgrade (server 403);
    // the containsProData comparison covers opening an already-locked case.
    const proDowngradeEnabled = useFeatureFlag('ProDowngrade');
    alimonyEnabledRef.current = useFeatureFlag('Alimony');
    const effectiveSubscriptionType = useEffectiveSubscriptionType();
    proDowngradeEnabledRef.current = proDowngradeEnabled;
    isTeamProRef.current = effectiveSubscriptionType === 'pro';
    const isProDowngradeReadOnly = proDowngradeLocked
        || isProDowngradeLocked(proDowngradeEnabled, effectiveSubscriptionType, containsProData);

    // BillingDunning: the team is read-only until it pays (from /auth/me), or a save was refused.
    const { isReadOnly: teamBillingReadOnly, canManageBilling } = useTeamBillingState();
    billingReadOnlyRef.current = teamBillingReadOnly;
    const isBillingReadOnly = teamBillingReadOnly || billingLocked;

    const taxYear = formState.data?.basic?.taxYear ?? currentCase?.case?.basic?.taxYear;
    const caseStateCode = formState.data?.basic?.stateCode ?? currentCase?.case?.basic?.stateCode;
    // Every read-only reason except the calc version lock. Only this blocks the tax year edit
    // that lifts the calc version lock.
    const isReadOnlyExceptCalcVersion = currentCase?.case?.basic?.caseStatus === 'finalized'
        || currentCase?.case?.basic?.caseStatus === 'archived'
        || currentCase?.case?.basic?.isLocked === true
        || (taxYear !== undefined && taxYear <= 2024)
        || isProDowngradeReadOnly
        || isBillingReadOnly;

    // Calc version availability (Configuration > Calculations). Not decided until the settings
    // have loaded, so a slow settings request never flashes the lock.
    const isAdminOrRoot = useHasAnyRole(['Admin', 'Root']);
    const { calcVersions, isLoading: systemSettingsLoading } = useSystemSettingsContext();
    calcVersionInputsRef.current = { ready: !systemSettingsLoading, calcVersions, isAdmin: isAdminOrRoot };
    const isCalcVersionLocked = !isAdminOrRoot && (
        (!systemSettingsLoading && isCalcVersionLockedFor(caseStateCode, taxYear, calcVersions, isAdminOrRoot))
        || (calcVersionRejectedKey !== null && calcVersionRejectedKey === `${taxYear}-${caseStateCode}`)
    );
    calcVersionLockedRef.current = isCalcVersionLocked;

    const isReadOnly = isReadOnlyExceptCalcVersion || isCalcVersionLocked;

    /**
     * True when an edit may be applied. While calc-version locked, the only edit allowed is the
     * one that moves the case to an enabled tax year (WizardHeader's tax year picker).
     */
    const canApplyEdit = useCallback((nextTaxYear?: number): boolean => {
        if (isReadOnlyExceptCalcVersion) return false;
        if (!isCalcVersionLocked) return true;
        return nextTaxYear !== undefined && isCalcVersionEnabled(caseStateCode, nextTaxYear, calcVersions);
    }, [isReadOnlyExceptCalcVersion, isCalcVersionLocked, caseStateCode, calcVersions]);

    // While a case is open this provider explains billing refusals itself, so the app-level
    // BillingReadOnlyGate stays quiet.
    useEffect(() => claimBillingReadOnlyModal(), []);

    // ConcurrentCaseEditGuard: mark/clear the "currently editing" homepage indicator. Only
    // arms once the loaded case matches the route's caseId (not mid-load-transition), and
    // never for read-only opens (viewing a finalized/archived/old case isn't "editing").
    const concurrentCaseEditGuardEnabled = useFeatureFlag('ConcurrentCaseEditGuard');
    concurrentCaseEditGuardEnabledRef.current = concurrentCaseEditGuardEnabled;
    const loadedCaseId = currentCase?.case?.basic?.caseId;
    const caseEditingLockCaseId = loadedCaseId && loadedCaseId === caseId ? caseId : undefined;
    const caseEditingLockEnabled = concurrentCaseEditGuardEnabled && !isReadOnly;
    // Live presence: while editing, poll the marker (visibility-gated, ~15s) and surface the
    // EditTakeoverModal if a DIFFERENT user takes over the case. Paused while the modal is
    // already up (pollEnabled: !takeoverBy) so it doesn't re-fire against the same takeover.
    useCaseEditingLock(caseEditingLockCaseId, caseEditingLockEnabled, {
        currentUserId: currentUser?.userId,
        currentUserFirstName: currentUser?.firstName,
        currentUserLastName: currentUser?.lastName,
        onTakenOver: (by) => {
            if (caseEditingLockCaseId) {
                caseApiService.logConcurrencyEvent(caseEditingLockCaseId, 'takeover_detected', `takenOverBy=${by.userId} tab=${by.tabId}`);
            }
            setTakeoverBy(by);
        },
        onClientEditingChange: setClientEditingActive,
        pollEnabled: !takeoverBy,
        pollIntervalMs: 15000,
    });

    // Explicit, awaitable release for navigation-driven exits (Cases/Settings/Admin buttons,
    // logout, back button -- see useNavigationGuard.ts). useCaseEditingLock's own unmount
    // cleanup ALSO clears the marker, but that fire-and-forget request races the next page's
    // own data fetch (e.g. HomePage's case list) -- fine on a fast local network, but a real,
    // reproducible race once real latency is involved (observed in test). Awaiting this before
    // navigate() guarantees the clear lands before the homepage asks "who's editing this case".
    const releaseCaseEditingLock = useCallback(async () => {
        if (!caseEditingLockEnabled || !caseEditingLockCaseId) return;
        try {
            await caseApiService.clearEditing(caseEditingLockCaseId);
        } catch (err) {
            debugError('releaseCaseEditingLock: failed to clear editing marker', err);
        }
    }, [caseEditingLockEnabled, caseEditingLockCaseId]);

    // EditTakeoverModal: another tab took over editing this case (a teammate, or another tab of
    // your own). "Take back control" re-marks the case as this tab's -- the marker flips back to
    // me (server) and an instant same-browser announce goes out -- so the other session then sees
    // this same modal -- then resumes detection. See docs/AUTOSAVE_DETAILS.md §17.
    const handleTakeBackControl = useCallback(async () => {
        if (caseEditingLockCaseId) {
            caseApiService.logConcurrencyEvent(caseEditingLockCaseId, 'take_back_control');
            await caseApiService.markEditing(caseEditingLockCaseId);
            announceTakeover(caseEditingLockCaseId, getTabId(), currentUser?.userId, currentUser?.firstName, currentUser?.lastName);
        }
        setTakeoverBy(null);
    }, [caseEditingLockCaseId, currentUser?.userId, currentUser?.firstName, currentUser?.lastName]);

    // "Return to homepage": leave the wizard. The provider unmounts, so useCaseEditingLock's
    // cleanup fires clearEditing -- which no-ops since the marker is no longer mine (correct).
    const handleTakeoverReturnHome = useCallback(() => {
        setTakeoverBy(null);
        navigate('/');
    }, [navigate]);

    const triggerTextInputBlur = useCallback(() => {
        // On blur, kick the debounce timer. Edits made during typing already set dirty
        // (without starting the timer), so the service has the right state to fire one save.
        BackgroundSaveService.markDirty({ startDebounce: true });
        setFormState(prev => ({
            ...prev,
            lastBlurTime: Date.now(),
            isEditingTextInput: false
        }));
    }, []);

    const updateField = useCallback(async (field: string, value: any, isTextInput: boolean = false) => {
        if (!canApplyEdit(field === 'basic' ? value?.taxYear : undefined)) {
            return;
        }

        const { recalculate } = await getCalcs();

        // R11: use prev.data inside the updater so we always see the latest
        // committed formState, not a potentially-stale closure snapshot.
        setFormState(prev => {
            const baseCase = prev.data || currentCase?.case;
            if (!baseCase) {
                return prev;
            }

            const mergedCase = mergeCaseData(baseCase, prev.data);
            if (!mergedCase) {
                return prev;
            }

            const updatedCase = {
                ...mergedCase,
                [field]: value
            };

            let recalculatedCase: CaseData;
            try {
                recalculatedCase = recalculate(updatedCase, { alimonyEnabled: alimonyEnabledRef.current });
                setCalculationError(null);
            } catch (calcError) {
                const error = calcError instanceof Error ? calcError : new Error(String(calcError));
                setCalculationError(error);
                if (error.message.includes('Unsupported state and year')) {
                    return { ...prev, data: updatedCase };
                }
                throw calcError;
            }

            setCurrentCase(prevCase => ({ ...prevCase!, case: recalculatedCase }));

            const next = { ...prev, data: recalculatedCase };
            if (isTextInput) next.isEditingTextInput = true;
            return next;
        });

        if (autoSaveEnabled && !isInitializing) {
            BackgroundSaveService.markDirty({ startDebounce: !isTextInput });
        }
    }, [canApplyEdit, currentCase, autoSaveEnabled, isInitializing]);

    const updateNestedField = useCallback(async (path: string, value: any, isTextInput: boolean = false) => {
        if (!canApplyEdit(path === 'basic.taxYear' ? value : undefined)) {
            return;
        }

        const { recalculate } = await getCalcs();

        // R11: all data computation (baseCase, mergedCase, path mutation, recalculate)
        // now runs inside setFormState(prev => ...) so it always reads the latest
        // committed prev.data, not a potentially-stale closure snapshot captured before
        // the async getCalcs() await returned.
        setFormState(prev => {
            const baseCase = prev.data || currentCase?.case;
            if (!baseCase) return prev;
            const mergedCase = mergeCaseData(baseCase, prev.data);
            if (!mergedCase) return prev;

            let newData: any;
            try {
                ({ newData } = applyNestedPathMutation(mergedCase, path, value));
            } catch (err) {
                debugError('SimpleCaseContext: Error in applyNestedPathMutation:', err);
                return prev;
            }

            let recalculatedCase: CaseData;
            try {
                recalculatedCase = recalculate(newData, { alimonyEnabled: alimonyEnabledRef.current });
                setCalculationError(null);
            } catch (calcError) {
                const error = calcError instanceof Error ? calcError : new Error(String(calcError));
                setCalculationError(error);
                if (error.message.includes('Unsupported state and year')) {
                    // Revert: return prev unchanged — prev.data already has the old
                    // value at the path; no need to manually re-apply previousValue.
                    return { ...prev, data: newData };
                }
                throw calcError;
            }

            setCurrentCase(prevCase => ({ ...prevCase!, case: recalculatedCase }));
            const next = { ...prev, data: recalculatedCase };
            if (isTextInput) next.isEditingTextInput = true;
            return next;
        });

        if (autoSaveEnabled && !isInitializing) {
            BackgroundSaveService.markDirty({ startDebounce: !isTextInput });
        }
    }, [canApplyEdit, currentCase, autoSaveEnabled, isInitializing]);

    const updateNestedFieldCallback = useCallback(async (callback: (mergedCase: CaseData) => CaseData, isTextInput: boolean = false) => {
        if (isReadOnly) {
            // Calc version lock only: allow the edit when it moves the case to an enabled year.
            let nextTaxYear: number | undefined;
            if (!isReadOnlyExceptCalcVersion && isCalcVersionLocked) {
                const base = formStateRef.current.data ?? currentCase?.case;
                try {
                    nextTaxYear = base ? callback(JSON.parse(JSON.stringify(base)))?.basic?.taxYear : undefined;
                } catch {
                    nextTaxYear = undefined;
                }
            }
            if (!canApplyEdit(nextTaxYear)) {
                return;
            }
        }

        const { recalculate } = await getCalcs();

        // R11: compute inside setFormState(prev => ...) for latest-data guarantee.
        setFormState(prev => {
            const baseCase = prev.data || currentCase?.case;
            if (!baseCase) return prev;
            const mergedCase = mergeCaseData(baseCase, prev.data);
            if (!mergedCase) return prev;

            let newData: any;
            try {
                newData = callback(JSON.parse(JSON.stringify(mergedCase)));
            } catch (err) {
                debugError('SimpleCaseContext: Error in updateNestedFieldCallback:', err);
                return prev;
            }

            let recalculatedCase: CaseData;
            try {
                recalculatedCase = recalculate(newData, { alimonyEnabled: alimonyEnabledRef.current });
                setCalculationError(null);
            } catch (calcError) {
                const error = calcError instanceof Error ? calcError : new Error(String(calcError));
                setCalculationError(error);
                if (error.message.includes('Unsupported state and year')) {
                    return prev;
                }
                throw calcError;
            }

            setCurrentCase(prevCase => ({ ...prevCase!, case: recalculatedCase }));
            const next = { ...prev, data: recalculatedCase };
            if (isTextInput) next.isEditingTextInput = true;
            return next;
        });

        if (autoSaveEnabled && !isInitializing) {
            BackgroundSaveService.markDirty({ startDebounce: !isTextInput });
        }
    }, [isReadOnly, isReadOnlyExceptCalcVersion, isCalcVersionLocked, canApplyEdit, currentCase, autoSaveEnabled, isInitializing]);

    // Force a flush: drains the BackgroundSaveService queue. The service reads the latest
    // formState via the registered snapshot provider, so we don't pass any args. The registered
    // onSuccess/onError handle etag/lastSaved/pendingServerChanges/error toast — no per-call plumbing.
    const forceSave = useCallback(async () => {
        if (!formStateRef.current.data?.basic?.caseId) {
            debugError('SimpleCaseContext: Cannot save - no case ID');
            return;
        }
        await BackgroundSaveService.flush();
    }, []);

    /**
     * Explicitly merge pending server-side changes (e.g. client-submitted expenses) into
     * the case. The MergePendingModal must show a blocking overlay during this call so
     * the user cannot edit form fields while the merge round-trip is in flight.
     * This is the ONLY path where server data is allowed to overwrite formState.data,
     * because it's user-initiated.
     */
    const triggerMerge = useCallback(async () => {
        if (isMerging) return;
        if (!formStateRef.current.data?.basic?.caseId) {
            debugError('SimpleCaseContext: Cannot merge - no case ID');
            return;
        }

        // Lock the modal first so the user is blocked from editing during the entire drain+merge.
        setIsMerging(true);

        try {
            // Drain any pending/in-flight auto-save first, so the merge sees a freshly-advanced
            // etag and the server has the latest client state. This prevents 412 races between
            // an in-flight save and the merge POST.
            await BackgroundSaveService.flush();

            const data = formStateRef.current.data;
            if (!data?.basic?.caseId) return;
            const caseId = data.basic.caseId;

            // Fix #3: if the merge 412s (the case changed since our cached etag -- e.g. a teammate
            // saved), refresh the etag to the latest and retry the merge ONCE. The merge overlays
            // the client's expenses onto the attorney's current model, so retrying preserves the
            // client's submission instead of dropping it (the old behavior surfaced SaveConflictModal,
            // whose Force Save re-saved the attorney's model WITHOUT the client expenses). Editing is
            // blocked (isMerging) throughout, so the attorney's model is stable across the retry.
            let response: Awaited<ReturnType<typeof caseApiService.mergeAndSaveCase>> | undefined;
            for (let attempt = 0; attempt < 2 && !response; attempt++) {
                try {
                    response = await caseApiService.mergeAndSaveCase(caseId, data, etagRef.current || '');
                } catch (mergeErr) {
                    if ((mergeErr as any)?.status === 412 && attempt === 0) {
                        caseApiService.logConcurrencyEvent(caseId, 'merge_conflict_retry', 'merge 412; refreshing etag and retrying');
                        const fresh = await caseApiService.getCaseNoMerge(caseId);
                        etagRef.current = fresh.etag;
                        continue; // retry with the refreshed etag
                    }
                    throw mergeErr;
                }
            }
            if (!response) return;

            etagRef.current = response.etag;

            const merged = response.data;
            setCurrentCase(prev => (prev ? { ...prev, case: merged, etag: response.etag } : { case: merged, etag: response.etag }));
            setFormState(prev => ({
                ...prev,
                data: merged,
                lastSaved: new Date().toLocaleTimeString()
            }));
            setPendingServerChanges(null);
            // Imported -- the client has submitted and left, so drop the "client is entering
            // expenses" indicator immediately rather than waiting for the ~15s poll / ~60s TTL.
            setClientEditingActive(false);
        } catch (error) {
            debugError('SimpleCaseContext: Merge failed:', error);
            // Still conflicting after a refresh+retry (a second concurrent change during the merge --
            // very rare). Keep the pending state so the user can retry the import, and surface a
            // clear message rather than the generic SaveConflictModal (whose Force Save would drop
            // the client's expenses). formState.data (the user's edits) is preserved. Fix #3.
            if ((error as any)?.status === 412) {
                addAlert('warning', 'The case changed while importing. Please review and import again.');
                BackgroundSaveService.markDirty({ startDebounce: false });
                return;
            }
            const errorMessage = extractDetailedErrorMessage(error);
            addAlert('error', errorMessage);
            throw error;
        } finally {
            setIsMerging(false);
        }
    }, [isMerging, addAlert]);

    // "Not Yet": hide the import prompt for now. Deliberately does NOT snooze -- the next autosave's
    // onSuccess (and the 412 branch) re-detect the still-pending submission and re-show the modal,
    // so the client's expenses keep being offered on every save until imported.
    const dismissPendingChanges = useCallback(() => {
        setPendingServerChanges(null);
    }, []);

    /**
     * Silently resolves a same-user save collision (see the onError 412 branch above): the
     * conflicting version was last saved by this same user's own other tab/device, not a
     * teammate's, so there's nothing to ask them to choose between -- just force-save behind a
     * lightweight non-dismissible "Saving..." modal (AutoSavingModal, rendered below) instead of
     * SaveConflictModal.
     *
     * 15s failsafe: if the save hasn't settled by then (e.g. a network stall/offline tab), stop
     * waiting and fall back to the normal SaveConflictModal so the user is never stuck staring
     * at the spinner forever. autoForceSaveSettledRef ensures only one of "flush finished" vs.
     * "failsafe fired" actually changes state -- whichever happens first wins.
     */
    const autoForceSave = useCallback(async () => {
        autoForceSaveSettledRef.current = false;
        const forceSaveCaseId = formStateRef.current.data?.basic?.caseId;
        if (forceSaveCaseId) {
            caseApiService.logConcurrencyEvent(forceSaveCaseId, 'same_user_force_save', 'silent auto-force-save over the user\'s own other tab');
        }
        setAutoForceSaving(true);

        if (autoForceSaveFailsafeRef.current) {
            clearTimeout(autoForceSaveFailsafeRef.current);
        }
        autoForceSaveFailsafeRef.current = setTimeout(() => {
            if (autoForceSaveSettledRef.current) return;
            autoForceSaveSettledRef.current = true;
            setAutoForceSaving(false);
            setSaveConflictActive(true);
        }, 15000);

        try {
            // Same unconditional-overwrite mechanism as resolveSaveConflict('forceSave') below:
            // no etag => the blob write is unconditional (docs/AUTOSAVE_DETAILS.md "Mental Model").
            etagRef.current = undefined;
            BackgroundSaveService.markDirty({ startDebounce: false });
            await BackgroundSaveService.flush();
        } catch (error) {
            if (!autoForceSaveSettledRef.current) {
                const msg = extractDetailedErrorMessage(error);
                addAlertRef.current('error', msg);
            }
        } finally {
            if (autoForceSaveFailsafeRef.current) {
                clearTimeout(autoForceSaveFailsafeRef.current);
                autoForceSaveFailsafeRef.current = null;
            }
            if (!autoForceSaveSettledRef.current) {
                autoForceSaveSettledRef.current = true;
                setAutoForceSaving(false);
            }
        }
    }, []);
    autoForceSaveRef.current = autoForceSave;

    /**
     * Resolves an active save conflict (SaveConflictModal) per the user's choice. Closes the
     * modal immediately in every branch -- the async work then proceeds in the background,
     * surfaced via the normal save-status UI (WizardHeader) rather than a modal spinner.
     */
    const resolveSaveConflict = useCallback(async (action: 'makeCopy' | 'forceSave' | 'cancel') => {
        setSaveConflictActive(false);

        if (action === 'cancel') {
            // Leave the edits marked dirty (unsaved) so hasUnsavedChanges / the nav guard keep
            // warning accurately -- nothing was persisted, so nothing should look "saved".
            BackgroundSaveService.markDirty({ startDebounce: false });
            addAlertRef.current('warning', 'Data is not saved; make changes at your own risk.');
            return;
        }

        if (action === 'forceSave') {
            // No etag => the blob write is unconditional (docs/AUTOSAVE_DETAILS.md "Mental
            // Model": "No ETag ⇒ unconditional last-write-wins"). This is the one path that
            // deliberately bypasses conflict detection and can overwrite the other session's
            // changes -- SaveConflictModal gates it behind its own second confirmation first.
            etagRef.current = undefined;
            BackgroundSaveService.markDirty({ startDebounce: false });
            await BackgroundSaveService.flush();
            return;
        }

        if (action === 'makeCopy') {
            // Preserve the user's current unsaved edits without touching the conflicting case at
            // all: clone the in-memory data onto a brand-new caseId and navigate into it exactly
            // like the "Add New" flow (HomePage) does, so the existing initializeNewCase route
            // effect picks it up. copyCase/CaseCopyService can't be reused here -- it always
            // re-reads the OTHER session's already-saved server blob by id, not this session's
            // unsaved in-memory data (see docs/AUTOSAVE_DETAILS.md "Make a Copy" note).
            const currentData = formStateRef.current.data;
            const oldCaseId = currentData?.basic?.caseId;
            if (!currentData || !oldCaseId) return;

            const clonedData: CaseData = JSON.parse(JSON.stringify(currentData));
            const newCaseId = crypto.randomUUID();
            clonedData.basic = {
                ...clonedData.basic,
                caseId: newCaseId,
                copiedFromCaseId: oldCaseId,
            };
            const sc = clonedData.basic?.stateCode ?? 'IA';

            // Abandoning the old case's failed save -- discard any stale in-flight/dirty state
            // tied to it before switching (same reset() loadCase itself does on a case switch).
            BackgroundSaveService.reset();
            addAlertRef.current('success', 'Your unsaved edits were preserved as a new case.');
            navigate(`/wizard/${sc}/${newCaseId}/case`, { state: { newCaseData: clonedData } });
            return;
        }
    }, [navigate]);

    const refreshCase = useCallback(async () => {
        if (currentCase?.case?.basic?.caseId) {
            await loadCase(currentCase.case.basic.caseId);
        }
    }, [currentCase, loadCase]);

    // Acknowledges a fatal case-load failure (CaseLoadErrorModal) and leaves the wizard. Without
    // this, most wizard pages' `if (loading || !formData)` guard renders a spinner forever on
    // load failure since formData never populates and their own `error` branch (if any) never
    // gets reached -- see CaseLoadErrorModal.tsx.
    const handleReturnHomeFromLoadError = useCallback(() => {
        setError(null);
        navigate('/');
    }, [navigate]);

    /**
     * ProDowngrade: "Make a Copy" from the locked modal. Goes through the normal copy endpoint, so
     * the server strips the Pro-only data and recalculates, then navigates to the editable copy.
     */
    const handleProDowngradeMakeCopy = useCallback(async (custodyArrangement: NonSplitCustodyArrangement | undefined, requestId: string) => {
        const lockedCaseId = formStateRef.current.data?.basic?.caseId;
        if (!lockedCaseId) {
            setProDowngradeLockedDismissed(true);
            return;
        }
        setProDowngradeCopying(true);
        try {
            const response = await caseApiService.copyCase(lockedCaseId, undefined, custodyArrangement, requestId);
            if (!response.success || !response.newCaseId) {
                throw new Error(response.message || 'Failed to copy case');
            }
            const stateCode = formStateRef.current.data?.basic?.stateCode ?? 'IA';
            navigate(`/wizard/${stateCode}/${response.newCaseId}/case`);
        } catch (err) {
            if (isCopyInProgressError(err)) {
                addAlertRef.current('info', copyInProgressMessage(err));
            } else if (isCalcVersionDisabledError(err)) {
                addAlertRef.current('error', (err as Error).message || 'That tax year is not available yet.');
            } else {
                debugError('SimpleCaseContext: ProDowngrade copy failed', err);
                addAlertRef.current('error', 'Failed to copy this case. Please try again from the case list.');
            }
            // Rethrown so the modal keeps this action's request id for a retry.
            throw err;
        } finally {
            setProDowngradeCopying(false);
        }
    }, [navigate]);

    const prevLocationRef = useRef(location.pathname);
    const isNavigatingRef = useRef(false);
    useEffect(() => {
        const isWizardPage = location.pathname.includes('/wizard/') && caseId;
        const locationChanged = prevLocationRef.current !== location.pathname;

        // In Manual Save mode, step-to-step wizard navigation must not silently save —
        // the user only saves via the Save FAB or an explicit prompt. The provider stays
        // mounted across wizard steps, so dirty state/formState.data survive untouched.
        if (isWizardPage && locationChanged && unsavedFromService && forceSave && !isNavigatingRef.current && !manualSaveMode) {
            isNavigatingRef.current = true;
            forceSave()
                .then(() => {})
                .catch(error => {
                    debugError('SimpleCaseContext: Failed to save before location change:', error);
                })
                .finally(() => {
                    isNavigatingRef.current = false;
                });
        }

        prevLocationRef.current = location.pathname;
    }, [location.pathname, unsavedFromService, forceSave, caseId, manualSaveMode]);

    const initializeNewCase = useCallback(async (caseData: CaseData) => {
        setLoading(false);
        setError(null);
        setIsInitializing(true);

        const { recalculate } = await getCalcs();
        let recalculatedCase: CaseData;
        try {
            recalculatedCase = recalculate(caseData, { alimonyEnabled: alimonyEnabledRef.current });
            setCalculationError(null);
        } catch (calcError) {
            const error = calcError instanceof Error ? calcError : new Error(String(calcError));
            setCalculationError(error);
            if (!error.message.includes('Unsupported state and year')) {
                throw calcError;
            }
            recalculatedCase = caseData;
        }

        etagRef.current = undefined;

        setCurrentCase({
            case: recalculatedCase,
            etag: undefined
        });

        setFormState({
            data: recalculatedCase,
            lastSaved: null,
            lastBlurTime: null,
            isEditingTextInput: false
        });

        // New case is unsaved by definition; mark dirty so the auto-save kicks in once we
        // exit the initializing window. Don't start the debounce yet — let the first edit
        // do that, or the navigation guard flush.
        BackgroundSaveService.markDirty({ startDebounce: false });

        setTimeout(() => {
            setIsInitializing(false);
        }, 2000);
    }, []);

    useEffect(() => {
        if (caseId) {
            const state = location.state as { newCaseData?: CaseData } | null;
            if (state?.newCaseData) {
                initializeNewCase(state.newCaseData);
                return;
            }

            const currentCaseId = currentCase?.case?.basic?.caseId;
            if (currentCaseId !== caseId) {
                loadCase(caseId);
            }
        }
    }, [caseId, loadCase, initializeNewCase, currentCase?.case?.basic?.caseId]);

    useEffect(() => {
        registerCalcsLoadFailure(setCalcsLoadError);
        return () => registerCalcsLoadFailure(null);
    }, []);

    const clearCalculationError = useCallback(() => {
        setCalculationError(null);
    }, []);

    const value: SimpleCaseContextType = {
        currentCase,
        loading,
        error,
        hasUnsavedChanges: unsavedFromService,
        isSaving: savingFromService,
        lastSaved: formState.lastSaved,
        formData: formState.data,
        autoSaveEnabled,
        isReadOnly,
        isProDowngradeReadOnly,
        // Exposed only when it is the sole reason, so the banner's "change the tax year to edit"
        // is never shown on a case that would stay read-only (finalized, billing, ProDowngrade).
        isCalcVersionLocked: isCalcVersionLocked && !isReadOnlyExceptCalcVersion,
        lastBlurTime: formState.lastBlurTime,
        isEditingTextInput: formState.isEditingTextInput,
        calculationError,
        calcsLoadError,
        toggleAutoSave,
        refreshCase,
        updateField,
        updateNestedField,
        updateNestedFieldCallback,
        triggerTextInputBlur,
        forceSave,
        initializeNewCase,
        clearCalculationError,
        pendingServerChanges,
        triggerMerge,
        dismissPendingChanges,
        isMerging,
        saveConflictActive,
        resolveSaveConflict,
        releaseCaseEditingLock
    };

    if (calculationError && !calculationError.message.includes('Unsupported state and year')) {
        throw calculationError;
    }

    // Fix #6: only ONE blocking modal shows at a time, by priority. A case-load failure blocks
    // everything; then a hard save conflict; then a takeover; then the transient auto-save
    // spinner; then the (least urgent, "Later"-able) import prompt. The ClientEditingIndicator is
    // a non-blocking pill and always shows.
    // ProDowngrade sits just under a load error: once the server has refused the save, no other
    // modal's resolution can help (retrying, merging, or taking over all end in the same 403), so
    // it outranks them. It only shows for the mid-session latch -- opening an already-locked case
    // is read-only from the start and needs no interruption.
    const loadErrorActive = !!error;
    const proDowngradeLockedShown = !loadErrorActive && proDowngradeLocked && !proDowngradeLockedDismissed;
    // BillingDunning ranks with ProDowngrade for the same reason: every other resolution ends in
    // the same 403.
    const billingLockedShown = !loadErrorActive && !proDowngradeLockedShown && billingLocked && !billingLockedDismissed;
    const blockingLockShown = proDowngradeLockedShown || billingLockedShown;
    const saveConflictShown = !loadErrorActive && !blockingLockShown && saveConflictActive;
    const takeoverShown = !loadErrorActive && !blockingLockShown && !saveConflictActive && !!takeoverBy;
    const autoSavingShown = !loadErrorActive && !blockingLockShown && !saveConflictActive && !takeoverBy && autoForceSaving;
    const mergePendingShown = !loadErrorActive && !blockingLockShown && !saveConflictActive && !takeoverBy && !autoForceSaving && !!pendingServerChanges;

    return (
        <SimpleCaseContext.Provider value={value}>
            {calcsLoadError && (
                <div
                    role="alert"
                    style={{
                        padding: '12px 16px',
                        backgroundColor: '#fef2f2',
                        color: '#991b1b',
                        borderBottom: '1px solid #fecaca',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: '8px',
                    }}
                >
                    <span>{calcsLoadError}</span>
                    <button
                        type="button"
                        onClick={() => window.location.reload()}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: '#b91c1c',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: 500,
                        }}
                    >
                        Refresh
                    </button>
                </div>
            )}
            {children}
            {/* Single-modal priority (fix #6): load-error > save-conflict > takeover > auto-save > import-prompt */}
            <CaseLoadErrorModal isOpen={loadErrorActive} message={error} onReturnHome={handleReturnHomeFromLoadError} />
            {saveConflictShown && <SaveConflictModal />}
            <EditTakeoverModal
                isOpen={takeoverShown}
                editorName={takeoverBy ? [takeoverBy.firstName, takeoverBy.lastName].filter(Boolean).join(' ').trim() || undefined : undefined}
                isSameUser={!!takeoverBy && !!currentUser?.userId && takeoverBy.userId.toLowerCase() === currentUser.userId.toLowerCase()}
                onTakeBackControl={handleTakeBackControl}
                onReturnHome={handleTakeoverReturnHome}
            />
            <AutoSavingModal isOpen={autoSavingShown} />
            {mergePendingShown && <MergePendingModal />}
            <ProDowngradeLockedModal
                isOpen={proDowngradeLockedShown}
                caseId={caseId}
                partyNames={{
                    partyA: formState.data?.parties?.partyA?.personal?.firstName,
                    partyB: formState.data?.parties?.partyB?.personal?.firstName,
                }}
                onMakeCopy={handleProDowngradeMakeCopy}
                onContinueViewOnly={() => setProDowngradeLockedDismissed(true)}
                isCopying={proDowngradeCopying}
            />
            <BillingReadOnlyModal
                isOpen={billingLockedShown}
                canManageBilling={canManageBilling}
                onClose={() => setBillingLockedDismissed(true)}
                onPayNow={() => {
                    setBillingLockedDismissed(true);
                    navigate(TEAM_BILLING_PATH);
                }}
            />
            <ClientEditingIndicator isVisible={clientEditingActive} />
        </SimpleCaseContext.Provider>
    );
};
